import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useTheme, usePermissions, notificationApi } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import EnvironmentSwitcher from '@/components/EnvironmentSwitcher';
import ClaretChatWidget from '@/components/ClaretChatWidget';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import {
    LayoutDashboard,
    Users,
    UserCircle,
    Phone,
    GraduationCap,
    DollarSign,
    Settings,
    LogOut,
    Bell,
    Moon,
    Sun,
    Monitor,
    Package,
    Cloud,
    Menu,
    X,
    Building2,
    BookOpen,
    Calculator,
    TrendingUp,
    Headphones,
    Shield,
    Inbox,
    UserCheck,
    UserMinus,
    Briefcase,
    Lock,
    FileText,
    Key,
    Home,
    Volume2,
    VolumeX,
    Clock,
    Calendar,
    BarChart3,
    Fingerprint,
    CheckSquare,
    ClipboardCheck,
    FolderOpen,
    UserCog,
    Receipt,
    Megaphone,
    FileCheck,
    ArrowRightLeft,
    Shuffle,
    Timer,
    ArrowDownRight,
    Upload,
    GitMerge,
    Server,
    Database,
    MessageSquare,
    Zap,
    Network,
    ListTodo,
    Sparkles,
    Brain,
    Target,
    PanelLeftClose,
    PanelLeftOpen,
    Award,
    AlertTriangle,
    RotateCcw,
    CalendarDays,
    CheckCircle,
    ShieldCheck,
    Activity,
    Download,
    BookText,
} from 'lucide-react';
import CLTLogo from '@/components/CLTLogo';
import { NotificationCenter } from '@/components/NotificationCenter';
import { registerServiceWorker, autoEnablePush, getPushState } from '@/lib/pushNotifications';
import logger from '@/lib/logger';

// Notification sound - programmatically generated loud alert
function playAlertSound(type = 'notification') {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const now = ctx.currentTime;

        if (type === 'lead') {
            // LOUD urgent triple-beep for new leads
            [0, 0.2, 0.4].forEach((offset) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'square';
                osc.frequency.value = 880;
                gain.gain.setValueAtTime(0.6, now + offset);
                gain.gain.exponentialRampToValueAtTime(0.01, now + offset + 0.15);
                osc.connect(gain).connect(ctx.destination);
                osc.start(now + offset);
                osc.stop(now + offset + 0.15);
            });
        } else {
            // LOUD double-tone alert for general notifications
            const osc1 = ctx.createOscillator();
            const osc2 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            const gain2 = ctx.createGain();

            osc1.type = 'square';
            osc1.frequency.value = 660;
            gain1.gain.setValueAtTime(0.5, now);
            gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
            osc1.connect(gain1).connect(ctx.destination);
            osc1.start(now);
            osc1.stop(now + 0.25);

            osc2.type = 'square';
            osc2.frequency.value = 990;
            gain2.gain.setValueAtTime(0.5, now + 0.3);
            gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.55);
            osc2.connect(gain2).connect(ctx.destination);
            osc2.start(now + 0.3);
            osc2.stop(now + 0.55);
        }
    } catch (e) {
        // Web Audio not supported — silent fallback
    }
}

// Helper to get notification type from notification data
function getNotificationType(notification) {
    if (!notification) return 'notification';
    const title = (notification.title || '').toLowerCase();
    const message = (notification.message || '').toLowerCase();
    const entityType = notification.entity_type || '';
    
    // Check if it's a lead-related notification
    if (entityType === 'lead' || title.includes('lead') || message.includes('lead') || message.includes('new lead')) {
        return 'lead';
    }
    return 'notification';
}

const SECTIONS = {
    sshr: {
        id: 'sshr',
        title: 'My Self-Service',
        icon: UserCog,
        color: 'bg-teal-500',
        roles: ['super_admin', 'coo', 'admin', 'sales_manager', 'team_leader', 'sales_executive', 'cs_head', 'cs_agent', 'mentor', 'academic_master', 'master_of_academics', 'master_of_academics_', 'hr', 'finance', 'finance_manager', 'operations', 'marketing', 'quality_control', 'business_development', 'business_development_manager_', 'business_development_manager', 'bd_executive', 'staff'],
        items: [
            { title: 'SSHR Dashboard', icon: UserCog, path: '/sshr' },
            { title: 'My Calendar', icon: Calendar, path: '/sshr/calendar' },
            { title: 'My Timesheet', icon: FileText, path: '/sshr/timesheet' },
            { title: 'Team Timesheets', icon: Users, path: '/hr/timesheet-team', roles: ['super_admin', 'admin', 'coo', 'hr', 'sales_manager', 'team_leader', 'cs_head', 'master_of_academics', 'master_of_academics_', 'marketing', 'marketing_admin'] },
            { title: 'Task Manager', icon: ListTodo, path: '/hr/tasks' },
            { title: 'My Payslips', icon: Receipt, path: '/sshr/payslips' },
            { title: 'Announcements', icon: Megaphone, path: '/sshr/announcements' },
        ],
    },
    sales: {
        id: 'sales',
        title: 'Sales',
        icon: Phone,
        color: 'bg-blue-500',
        roles: ['super_admin', 'admin', 'sales_manager', 'team_leader', 'sales_executive'],
        items: [
            { title: 'Sales CRM', icon: Phone, path: '/sales' },
            { title: 'Sales Dashboard', icon: TrendingUp, path: '/sales/dashboard' },
            { title: 'Lead Analysis', icon: BarChart3, path: '/analytics/lead-analysis', roles: ['super_admin', 'admin', 'marketing', 'marketing_admin', 'sales_manager', 'coo', 'ceo'] },
            { title: 'Dormant Leads', icon: AlertTriangle, path: '/sales/dormant-leads', roles: ['super_admin', 'admin', 'sales_manager'] },
            { title: 'Returning Leads', icon: RotateCcw, path: '/sales/returning-leads', roles: ['super_admin', 'admin', 'sales_manager'] },
            { title: 'Assignment Audit', icon: Activity, path: '/sales/assignment-audit', roles: ['super_admin', 'admin', 'sales_manager', 'team_leader', 'ceo'] },
            { title: 'Productivity (Live)', icon: Activity, path: '/productivity', roles: ['super_admin', 'admin', 'sales_manager', 'team_leader', 'ceo', 'coo'] },
            { title: 'Call Analytics', icon: BarChart3, path: '/call-analytics' },
            { title: 'Call Intelligence', icon: Phone, path: '/call-intelligence', roles: ['super_admin', 'admin', 'sales_manager', 'team_leader', 'ceo', 'coo'] },
            { title: 'Script Learning', icon: TrendingUp, path: '/script-learning', roles: ['super_admin', 'admin', 'sales_manager', 'ceo', 'coo'] },
            { title: 'Sales Directory', icon: Users, path: '/sales/directory', roles: ['super_admin', 'admin', 'sales_manager'] },
            { title: 'My Commissions', icon: DollarSign, path: '/commission-dashboard' },
            { title: "Today's Follow-ups", icon: Bell, path: '/followups' },
            { title: 'Historical Import', icon: Upload, path: '/sales/historical-import', roles: ['super_admin'] },
        ],
    },
    cs: {
        id: 'cs',
        title: 'Customer Service',
        icon: Headphones,
        color: 'bg-emerald-500',
        roles: ['super_admin', 'admin', 'cs_head', 'cs_agent'],
        items: [
            { title: 'Customer Service', icon: Users, path: '/cs' },
            { title: 'CS Dashboard', icon: Headphones, path: '/cs/dashboard' },
            { title: 'CS Productivity Live', icon: Activity, path: '/productivity?scope=cs', roles: ['super_admin', 'admin', 'cs_head'] },
            { title: 'My Commissions', icon: DollarSign, path: '/commission-dashboard', roles: ['super_admin', 'admin', 'cs_head', 'cs_agent'] },
            { title: 'Student Directory', icon: Database, path: '/cs/directory', roles: ['super_admin', 'admin', 'cs_head'] },
            { title: 'Student Portal', icon: GraduationCap, path: '/cs/student-portal', roles: ['super_admin', 'admin', 'cs_head'] },
            { title: 'Merge Approvals', icon: GitMerge, path: '/cs/merge-approvals', roles: ['super_admin', 'admin', 'cs_head'] },
            { title: 'CS Historical Import', icon: Upload, path: '/cs/historical-import', roles: ['super_admin'] },
            { title: 'Call Log', icon: Phone, path: '/call-log', roles: ['super_admin', 'admin', 'cs_head', 'cs_agent'] },
        ],
    },
    academics: {
        id: 'academics',
        title: 'Academics',
        icon: GraduationCap,
        color: 'bg-orange-500',
        roles: ['super_admin', 'admin', 'mentor', 'academic_master', 'master_of_academics', 'master_of_academics_', 'business_development', 'business_development_manager_', 'business_development_manager', 'bd_executive', 'cs_head'],
        items: [
            { title: 'Mentor CRM', icon: GraduationCap, path: '/mentor', roles: ['super_admin', 'admin', 'mentor', 'academic_master', 'master_of_academics', 'master_of_academics_', 'cs_head'] },
            { title: 'Mentor Dashboard', icon: TrendingUp, path: '/mentor/dashboard', roles: ['super_admin', 'admin', 'mentor', 'academic_master', 'master_of_academics', 'master_of_academics_', 'cs_head'] },
            { title: 'Leaderboard', icon: Users, path: '/mentor/leaderboard', roles: ['super_admin', 'admin', 'mentor', 'academic_master', 'master_of_academics', 'master_of_academics_', 'cs_head'] },
            { title: 'Cross-Mentor Deposits', icon: ArrowRightLeft, path: '/mentor/cross-deposits', roles: ['super_admin', 'admin', 'mentor', 'academic_master', 'master_of_academics', 'master_of_academics_', 'cs_head'] },
            { title: 'Call Log', icon: Phone, path: '/call-log', roles: ['super_admin', 'admin', 'mentor', 'academic_master', 'master_of_academics', 'master_of_academics_'] },
            { title: 'BD CRM', icon: Briefcase, path: '/bd', roles: ['super_admin', 'admin', 'business_development', 'business_development_manager_', 'business_development_manager', 'bd_executive'] },
            { title: 'BD Dashboard', icon: TrendingUp, path: '/bd/dashboard', roles: ['super_admin', 'admin', 'business_development', 'business_development_manager_', 'business_development_manager', 'bd_executive'] },
            { title: 'BD Calculator', icon: Calculator, path: '/bd/calculator', roles: ['super_admin', 'admin', 'business_development', 'business_development_manager_', 'business_development_manager', 'bd_executive'] },
            { title: 'Historical Import', icon: Upload, path: '/mentor/historical-import', roles: ['super_admin'] },
        ],
    },
    hr: {
        id: 'hr',
        title: 'Human Resources',
        icon: Users,
        color: 'bg-rose-500',
        // Section-level gate: any role that needs to access ANY HR sub-item
        // must be listed here. Per-item `roles` then narrow down what each
        // role actually sees (e.g., team_leader only sees Leave/Regularization
        // Approvals + Team Timesheets, not full HR admin pages).
        roles: ['super_admin', 'admin', 'hr', 'sales_manager', 'team_leader',
                'cs_head', 'marketing_admin', 'master_of_academics', 'master_of_academics_',
                'finance', 'coo'],
        tabs: [
            { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { id: 'people', label: 'People', icon: UserCircle },
            { id: 'attendance', label: 'Attendance', icon: Clock },
            { id: 'leave', label: 'Leave Mgmt', icon: Calendar },
            { id: 'payroll', label: 'Payroll', icon: DollarSign },
            { id: 'performance', label: 'Performance', icon: TrendingUp },
            { id: 'timesheet', label: 'Timesheet', icon: FileText },
            { id: 'shifts', label: 'Shifts', icon: Clock },
        ],
        items: [
            // Dashboard tab
            { title: 'HR Dashboard', icon: LayoutDashboard, path: '/hr/dashboard', tab: 'dashboard', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'HR Analytics', icon: BarChart3, path: '/hr/analytics', tab: 'dashboard', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'HR Reports', icon: FileText, path: '/hr/reports', tab: 'dashboard', roles: ['super_admin', 'admin', 'hr'] },
            // People tab
            { title: 'Employee Master', icon: UserCircle, path: '/hr/employees', tab: 'people', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Offboarding', icon: UserMinus, path: '/hr/offboarding', tab: 'people', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Company Documents', icon: FolderOpen, path: '/hr/documents', tab: 'people', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Employee Value', icon: Award, path: '/hr/employee-value', tab: 'people', roles: ['super_admin', 'admin', 'hr'] },
            // Attendance tab
            { title: 'Attendance', icon: Clock, path: '/hr/attendance', tab: 'attendance', roles: ['super_admin', 'admin', 'hr'] },
            { title: "Who's In", icon: UserCheck, path: '/hr/whos-in', tab: 'attendance', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Attendance Settings', icon: Settings, path: '/hr/attendance-settings', tab: 'attendance', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'BioCloud Sync', icon: Fingerprint, path: '/hr/biocloud', tab: 'attendance', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Attendance Gap', icon: AlertTriangle, path: '/hr/attendance-gap', tab: 'attendance', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Bulk Attendance', icon: CalendarDays, path: '/hr/bulk-attendance', tab: 'attendance', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Applied Regularization', icon: ClipboardCheck, path: '/hr/regularization', tab: 'attendance', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Regularization Approvals', icon: CheckCircle, path: '/hr/regularization-approvals', tab: 'attendance', roles: ['super_admin', 'admin', 'hr', 'sales_manager', 'team_leader', 'cs_head', 'marketing_admin', 'master_of_academics', 'master_of_academics_', 'finance', 'coo'] },
            { title: 'Approval Pipeline', icon: GitMerge, path: '/hr/approval-pipeline', tab: 'attendance', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'WFH Verification', icon: Home, path: '/hr/wfh-verification', tab: 'attendance', roles: ['super_admin', 'admin', 'hr'] },
            // Leave tab
            { title: 'Leave Balance', icon: Calendar, path: '/hr/leave-balance', tab: 'leave', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Applied Leaves', icon: FileText, path: '/hr/leave', tab: 'leave', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Leave Approvals', icon: CheckCircle, path: '/hr/leave-approvals', tab: 'leave', roles: ['super_admin', 'admin', 'hr', 'sales_manager', 'team_leader', 'cs_head', 'marketing_admin', 'master_of_academics', 'master_of_academics_', 'finance', 'coo'] },
            // Payroll tab
            { title: 'Pre-Payroll Verification', icon: ShieldCheck, path: '/hr/payroll/pre-run', tab: 'payroll', roles: ['super_admin', 'admin', 'hr', 'finance'] },
            { title: 'Payroll Engine', icon: DollarSign, path: '/hr/payroll', tab: 'payroll', roles: ['super_admin', 'admin', 'hr'] },
            // Performance tab
            { title: 'Performance', icon: TrendingUp, path: '/hr/performance', tab: 'performance', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Department Discipline', icon: BarChart3, path: '/hr/department-performance', tab: 'performance', roles: ['super_admin', 'admin', 'hr'] },
            // Timesheet tab
            { title: 'HR Timesheet Review', icon: FileText, path: '/hr/timesheet', tab: 'timesheet', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'My Team Timesheets', icon: Users, path: '/hr/timesheet-team', tab: 'timesheet', roles: ['super_admin', 'admin', 'hr', 'sales_manager', 'team_leader', 'cs_head', 'master_of_academics', 'marketing', 'marketing_admin'] },
            // Shifts tab
            { title: 'Shift Master', icon: Clock, path: '/hr/shift-master', tab: 'shifts', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Employee Shift Assignment', icon: Users, path: '/hr/shift-assignment', tab: 'shifts', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Late Rules', icon: AlertTriangle, path: '/hr/late-rules', tab: 'shifts', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Mentor Roster', icon: CalendarDays, path: '/hr/mentor-roster', tab: 'shifts', roles: ['super_admin', 'admin', 'hr'] },
        ],
    },
    operations: {
        id: 'operations',
        title: 'Operations',
        icon: Briefcase,
        color: 'bg-purple-500',
        roles: ['super_admin', 'admin', 'sales_manager', 'cs_head', 'finance', 'hr', 'operations', 'team_leader', 'master_of_academics', 'master_of_academics_'],
        items: [
            { title: 'QC Dashboard', icon: Headphones, path: '/qc-dashboard', roles: ['super_admin', 'admin', 'cs_head', 'sales_manager'] },
            { title: 'Leads Pool', icon: Inbox, path: '/leads/pool', roles: ['super_admin', 'admin', 'coo', 'sales_manager'] },
            { title: 'Approvals', icon: CheckSquare, path: '/approvals', roles: ['super_admin', 'admin', 'sales_manager', 'team_leader'] },
            { title: 'Transfer Requests', icon: ArrowRightLeft, path: '/transfer-requests', roles: ['super_admin', 'admin', 'sales_manager', 'team_leader'] },
            { title: 'Round Robin', icon: Shuffle, path: '/round-robin', roles: ['super_admin', 'admin', 'sales_manager'] },
            { title: 'SLA Management', icon: Timer, path: '/sla-management', roles: ['super_admin', 'admin', 'sales_manager'] },
            { title: 'Customer Master', icon: UserCheck, path: '/customers', roles: ['super_admin', 'admin', 'cs_head', 'finance'] },
            { title: 'Duplicate Manager', icon: GitMerge, path: '/operations/duplicate-manager', roles: ['super_admin', 'admin', 'sales_manager', 'cs_head', 'master_of_academics', 'master_of_academics_'] },
            { title: 'Departments', icon: Building2, path: '/departments', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Courses', icon: BookOpen, path: '/courses', roles: ['super_admin', 'admin', 'sales_manager'] },
            { title: 'Certificates', icon: FileText, path: '/certificates', roles: ['super_admin', 'admin', 'operations', 'cs_head'] },
            { title: 'MT5 Integration', icon: Server, path: '/mt5', roles: ['super_admin', 'admin', 'cs_head', 'cs_head_', 'customer_service', 'customer_service_'] },
            { title: 'Real-Time Assist', icon: Zap, path: '/realtime-assist', roles: ['super_admin', 'admin', 'sales_manager', 'team_leader', 'sales_executive', 'ceo', 'coo'] },
        ],
    },
    security: {
        id: 'security',
        title: 'Security',
        icon: Lock,
        color: 'bg-red-500',
        roles: ['super_admin', 'admin', 'hr', 'sales_manager'],
        items: [
            { title: 'Access Control', icon: Shield, path: '/access-control', roles: ['super_admin'] },
            { title: 'Role Management', icon: Users, path: '/roles', roles: ['super_admin'] },
            { title: 'Teams Management', icon: Users, path: '/teams', roles: ['super_admin', 'admin', 'sales_manager'] },
            { title: 'Password Resets', icon: Key, path: '/password-resets', roles: ['super_admin'] },
            { title: 'Audit Log', icon: FileText, path: '/audit-log', roles: ['super_admin', 'admin'] },
            { title: 'User Management', icon: UserCircle, path: '/users', roles: ['super_admin', 'admin', 'hr'] },
            { title: 'Admin Exports', icon: Download, path: '/admin-exports', roles: ['super_admin', 'admin', 'sales_manager'] },
            { title: 'Developer Guide', icon: BookText, path: '/developer-guide', roles: ['super_admin', 'admin'] },
            { title: 'Claret Configuration', icon: Settings, path: '/claret/config', roles: ['super_admin', 'sales_manager'] },
            { title: 'Objection Library', icon: Shield, path: '/claret/objections', roles: ['super_admin', 'admin', 'sales_manager'] },
        ],
    },
    finance: {
        id: 'finance',
        title: 'Finance',
        icon: DollarSign,
        color: 'bg-cyan-500',
        roles: ['super_admin', 'admin', 'finance', 'ceo'],
        items: [
            { title: 'Finance', icon: DollarSign, path: '/finance', roles: ['super_admin', 'admin', 'finance'] },
            { title: 'Commission Engine', icon: Calculator, path: '/commissions', roles: ['super_admin', 'admin', 'finance'] },
            { title: 'Commission Dashboard', icon: TrendingUp, path: '/commission-dashboard', roles: ['super_admin', 'admin', 'finance'] },
            { title: 'Mentor Withdrawals', icon: ArrowDownRight, path: '/finance/mentor-withdrawals', roles: ['super_admin', 'admin', 'finance'] },
        ],
    },
    marketing: {
        id: 'marketing',
        title: 'Marketing',
        icon: Megaphone,
        color: 'bg-pink-500',
        roles: ['super_admin', 'admin', 'marketing', 'marketing_admin'],
        items: [
            { title: 'Analytics Dashboard', icon: BarChart3, path: '/marketing/dashboard' },
            { title: 'Competitor Intel', icon: Target, path: '/competitor-intelligence' },
            { title: 'Content Calendar', icon: Calendar, path: '/marketing/calendar' },
            { title: 'Meta Hub', icon: BarChart3, path: '/meta-hub' },
            { title: 'Market Analysis', icon: TrendingUp, path: '/marketing/analysis' },
            { title: 'Content Studio', icon: Sparkles, path: '/marketing/content-studio' },
            { title: 'Lead Connectors', icon: Inbox, path: '/marketing/connectors' },
            { title: 'Marketing Toolkit', icon: Sparkles, path: '/marketing/toolkit', roles: ['super_admin', 'admin', 'marketing_admin', 'marketing_manager', 'marketing'] },
            { title: 'Settings', icon: Settings, path: '/marketing/settings', roles: ['super_admin', 'admin'] },
        ],
    },
    chat: {
        id: 'chat',
        title: 'Team Chat',
        icon: MessageSquare,
        color: 'bg-blue-600',
        roles: ['super_admin', 'admin', 'sales_manager', 'team_leader', 'sales_executive', 'cs_head', 'cs_agent', 'mentor', 'academic_master', 'master_of_academics', 'master_of_academics_', 'hr', 'finance', 'finance_manager', 'operations', 'marketing', 'marketing_admin', 'quality_control', 'business_development', 'business_development_manager_', 'business_development_manager', 'staff'],
        items: [
            { title: 'Team Chat', icon: MessageSquare, path: '/chat' },
        ],
    },
    it: {
        id: 'it',
        title: 'IT & Assets',
        icon: Monitor,
        color: 'bg-slate-600',
        roles: ['super_admin', 'coo', 'admin', 'hr', 'sales_manager', 'team_leader', 'sales_executive', 'finance', 'operations', 'marketing', 'quality_control', 'staff'],
        items: [
            { title: 'Asset Management', icon: Package, path: '/it/asset-management', roles: ['super_admin', 'admin', 'hr', 'operations', 'coo', 'ceo'] },
            { title: 'SaaS Subscriptions', icon: Cloud, path: '/it/saas', roles: ['super_admin', 'admin', 'hr', 'operations', 'coo', 'ceo', 'finance'] },
            { title: 'IT Assets (legacy)', icon: Monitor, path: '/it/assets' },
        ],
    },
    executive: {
        id: 'executive',
        title: 'Executive',
        icon: Zap,
        color: 'bg-amber-500',
        roles: ['super_admin', 'coo', 'admin', 'hr'],
        items: [
            { title: 'Executive Dashboard', icon: Zap, path: '/executive', roles: ['super_admin', 'coo', 'admin'] },
            { title: 'Organization Map', icon: Network, path: '/organization' },
            { title: 'Productivity Report', icon: BarChart3, path: '/reports/productivity', roles: ['super_admin', 'coo', 'admin', 'hr', 'team_leader', 'cs_head', 'master_of_academics'] },
            { title: 'Report Scheduler', icon: Calendar, path: '/reports/scheduler', roles: ['super_admin', 'coo', 'admin'] },
            { title: 'Custom Reports', icon: Database, path: '/reports', roles: ['super_admin', 'coo', 'admin'] },
        ],
    },
};

function SectionIcon({ section, onClick }) {
    const Icon = section.icon;
    return (
        <button
            onClick={onClick}
            className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-card border border-border hover:border-primary/50 hover:shadow-lg transition-all duration-300 group"
            data-testid={`section-icon-${section.id}`}
        >
            <div className={`w-16 h-16 rounded-2xl ${section.color} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform`}>
                <Icon className="h-8 w-8" />
            </div>
            <span className="font-medium text-sm">{section.title}</span>
        </button>
    );
}

function SidebarNavItem({ item, isActive, onClick }) {
    const Icon = item.icon;
    return (
        <button
            onClick={onClick}
            className={`sidebar-item w-full ${isActive ? 'active' : ''}`}
            data-testid={`nav-${item.title.toLowerCase().replace(/['\s]/g, '-')}`}
        >
            <Icon className="h-5 w-5 flex-shrink-0" />
            <span className="truncate">{item.title}</span>
        </button>
    );
}

import { PhoneCall as PhoneCallIcon } from 'lucide-react';

function TalkTimeWidget() {
    const { user } = useAuth();
    const [stats, setStats] = React.useState(null);
    const ext = user?.threecx_extension;

    React.useEffect(() => {
        let mounted = true;
        const load = () => {
            api.get('/3cx/my-stats').then(r => { if (mounted) setStats(r.data); }).catch(() => {});
        };
        load();
        const interval = setInterval(load, 120000);
        return () => { mounted = false; clearInterval(interval); };
    }, []);

    const mins = stats?.minutes || 0;
    const color = mins >= 90 ? 'bg-emerald-500 text-white' : mins >= 60 ? 'bg-amber-400 text-amber-950' : 'bg-red-500 text-white';

    return (
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${color} transition-colors`}
            title={ext ? `${stats?.calls || 0} calls today (${stats?.inbound || 0} in, ${stats?.outbound || 0} out)` : 'No 3CX extension mapped'}
            data-testid="talk-time-widget">
            <PhoneCallIcon className="h-3.5 w-3.5" />
            <span>{stats?.display || '0m'}</span>
        </div>
    );
}

function Layout() {
    const [activeSection, setActiveSection] = useState(null);
    const [activeHrTab, setActiveHrTab] = useState('dashboard');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        return localStorage.getItem('sidebarCollapsed') === 'true';
    });
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [chatUnread, setChatUnread] = useState(0);
    const [myStatus, setMyStatus] = useState('online');
    const prevUnreadCountRef = useRef(0);
    const prevNotificationsRef = useRef([]);
    
    // Get notification sound setting from localStorage
    const [soundEnabled, setSoundEnabled] = useState(() => {
        const saved = localStorage.getItem('notificationSoundEnabled');
        return saved !== null ? JSON.parse(saved) : true;
    });

    // Sync sound settings with backend
    const syncSoundSettings = useCallback(async (enabled) => {
        try {
            await api.put('/users/preferences', { notification_sound_enabled: enabled });
        } catch (err) {
            logger.error('Failed to sync sound settings:', err);
        }
    }, []);

    // Play appropriate sound when new notifications arrive
    useEffect(() => {
        if (!soundEnabled) return;
        if (unreadCount <= prevUnreadCountRef.current) {
            prevUnreadCountRef.current = unreadCount;
            return;
        }
        
        // Find new notifications by comparing with previous
        const prevIds = new Set(prevNotificationsRef.current.map(n => n.id));
        const newNotifications = notifications.filter(n => !prevIds.has(n.id) && !n.read);
        
        if (newNotifications.length > 0) {
            // Play LOUD alert sound based on notification type
            const hasLeadNotification = newNotifications.some(n => getNotificationType(n) === 'lead');
            playAlertSound(hasLeadNotification ? 'lead' : 'notification');
        }
        
        prevUnreadCountRef.current = unreadCount;
        prevNotificationsRef.current = [...notifications];
    }, [unreadCount, notifications, soundEnabled]);
    
    const { user, logout } = useAuth();
    const { theme, themeMode, toggleTheme } = useTheme();
    const { canAccess, canAccessModule, loading: permLoading } = usePermissions();
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname;
    const userRole = user?.role || '';

    // === Hidden access: Cmd+D → password gate ===
    const [_sa, _setSa] = useState(false);
    const [_sp, _setSp] = useState(false);
    const _spRef = useRef(null);
    const _saTimer = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
                e.preventDefault();
                if (userRole === 'super_admin' && !_sa) {
                    _setSp(true);
                    setTimeout(() => _spRef.current?.focus(), 100);
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [userRole, _sa]);

    const _handleSp = (e) => {
        e.preventDefault();
        const val = e.target.elements?.sp?.value;
        if (val === '@Atifa2605') {
            _setSa(true);
            _setSp(false);
            sessionStorage.setItem('_x', Date.now().toString());
            if (_saTimer.current) clearTimeout(_saTimer.current);
            _saTimer.current = setTimeout(() => { _setSa(false); sessionStorage.removeItem('_x'); }, 5 * 60 * 1000);
        } else {
            _setSp(false);
        }
    };

    useEffect(() => { return () => { if (_saTimer.current) clearTimeout(_saTimer.current); }; }, []);

    useEffect(() => {
        async function fetchNotifications() {
            try {
                const response = await notificationApi.getAll();
                const data = response.data;
                const items = data?.items || (Array.isArray(data) ? data : []);
                setNotifications(items);
                setUnreadCount(data?.unread_count || items.filter(n => !n.read).length);
            } catch (err) { /* ignore */ }
        }
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, []);

    // Heartbeat + chat unread count + in-app toast on incoming messages.
    // - Bumped poll from 60s → 8s so the bell badge + toast feel real-time.
    // - When the unread count increases (and we're not already on /chat),
    //   surface a sonner toast preview of the latest message.
    const prevChatUnreadRef = useRef(0);
    const lastSeenMessageIdRef = useRef(null);
    useEffect(() => {
        let stopped = false;

        async function heartbeatAndChatUnread() {
            try {
                await api.post('/chat/heartbeat');
                const res = await api.get('/chat/unread-count');
                if (stopped) return;
                const newUnread = res.data?.unread || res.data?.unread_count || 0;
                const prevUnread = prevChatUnreadRef.current;
                setChatUnread(newUnread);

                // Only show a toast when (a) the unread count grew, and
                // (b) the user isn't already inside the chat page reading.
                const onChatPage = (window.location.pathname || '').startsWith('/chat');
                if (newUnread > prevUnread && prevUnread > 0 && !onChatPage) {
                    // Pull the latest unread message preview for a useful toast.
                    try {
                        const previewRes = await api.get('/chat/conversations');
                        const convos = previewRes.data || [];
                        // Pick the conversation with the most recent activity that has unread
                        const candidates = convos
                            .filter((c) => (c.unread_count || 0) > 0)
                            .sort((a, b) => (b.last_message_at || '').localeCompare(a.last_message_at || ''));
                        const target = candidates[0];
                        if (target && target.id && target.last_message_at !== lastSeenMessageIdRef.current) {
                            lastSeenMessageIdRef.current = target.last_message_at;
                            const senderLabel = target.is_group
                                ? (target.group_name || 'Group chat')
                                : (target.last_message_sender || target.display_name || 'New message');
                            const body = target.last_message_preview || '';
                            toast(senderLabel, {
                                description: body,
                                action: {
                                    label: 'Open',
                                    onClick: () => navigate(`/chat?conversation=${target.id}`),
                                },
                                duration: 6000,
                            });
                        }
                    } catch { /* ignore preview failure — badge still updates */ }
                }
                prevChatUnreadRef.current = newUnread;

                const pres = await api.get('/user/presence');
                if (stopped) return;
                const me = (pres.data?.users || []).find(u => u.id === user?.id);
                if (me) setMyStatus(me.status);
            } catch { /* ignore */ }
        }
        heartbeatAndChatUnread();
        const hbInterval = setInterval(heartbeatAndChatUnread, 8000);
        return () => { stopped = true; clearInterval(hbInterval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Service worker registration + auto-enable Web Push.
    // Re-prompts every 24h until the user is subscribed, OR they explicitly
    // disable in Settings, OR the browser hardens to 'denied'. Also re-tries
    // when the tab regains focus (>1h since last attempt) to catch people
    // who clicked Allow in browser settings but didn't refresh.
    useEffect(() => {
        const LAST_ATTEMPT_KEY = 'clt_push_last_attempt_at';
        const HOUR = 60 * 60 * 1000;
        const DAY = 24 * HOUR;

        const tryEnable = async (force = false) => {
            try {
                const state = await getPushState();
                if (state.subscribed) return;          // already done
                if (state.userDisabled) return;        // explicitly turned off in Settings
                if (state.permission === 'denied') return;  // browser-blocked
                const last = parseInt(localStorage.getItem(LAST_ATTEMPT_KEY) || '0', 10);
                const gap = force ? HOUR : DAY;
                if (last && Date.now() - last < gap) return;
                localStorage.setItem(LAST_ATTEMPT_KEY, String(Date.now()));
                await autoEnablePush();
            } catch (e) { /* silent — toggle in Settings still works */ }
        };

        registerServiceWorker();
        // Run immediately on mount, no artificial delay.
        tryEnable();
        // Re-check every 24h within a long-lived session.
        const daily = setInterval(() => tryEnable(), DAY);
        // Re-check when the user comes back to the tab (>1h gap).
        const onFocus = () => tryEnable(true);
        window.addEventListener('focus', onFocus);

        if (!('serviceWorker' in navigator)) {
            return () => {
                clearInterval(daily);
                window.removeEventListener('focus', onFocus);
            };
        }
        const handleSwMessage = (e) => {
            const msg = e.data || {};
            if (msg.type === 'CLT_PUSH_NAVIGATE' && msg.url) {
                navigate(msg.url);
            }
        };
        navigator.serviceWorker.addEventListener('message', handleSwMessage);
        return () => {
            clearInterval(daily);
            window.removeEventListener('focus', onFocus);
            navigator.serviceWorker.removeEventListener('message', handleSwMessage);
        };
    }, [navigate]);

    useEffect(() => {
        if (currentPath === '/home' || currentPath === '/') {
            setActiveSection(null);
            return;
        }
        
        // First: check if the path exists in the CURRENTLY active section (don't switch away)
        if (activeSection) {
            const currentSec = SECTIONS[activeSection];
            if (currentSec) {
                for (let j = 0; j < currentSec.items.length; j++) {
                    if (currentPath === currentSec.items[j].path || currentPath.startsWith(currentSec.items[j].path + '/')) {
                        return; // Stay in current section
                    }
                }
            }
        }

        // Otherwise: find the best matching section for this path
        const sectionKeys = Object.keys(SECTIONS);
        for (let i = 0; i < sectionKeys.length; i++) {
            const key = sectionKeys[i];
            const section = SECTIONS[key];
            for (let j = 0; j < section.items.length; j++) {
                if (currentPath === section.items[j].path || currentPath.startsWith(section.items[j].path + '/')) {
                    setActiveSection(key);
                    return;
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath]);

    // Auto-detect active HR tab from current path
    useEffect(() => {
        if (activeSection !== 'hr') return;
        const hrSection = SECTIONS.hr;
        if (!hrSection?.tabs) return;
        for (let i = 0; i < hrSection.items.length; i++) {
            const item = hrSection.items[i];
            if (currentPath === item.path || currentPath.startsWith(item.path + '/')) {
                if (item.tab && item.tab !== activeHrTab) {
                    setActiveHrTab(item.tab);
                }
                return;
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath, activeSection]);

    function handleLogout() {
        logout();
        toast.success('Logged out successfully');
        navigate('/login');
    }

    function goTo(path) {
        navigate(path);
        setMobileMenuOpen(false);
    }

    function goHome() {
        setActiveSection(null);
        navigate('/home');
    }

    function toggleSidebar() {
        setSidebarCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('sidebarCollapsed', String(next));
            return next;
        });
    }

    function selectSection(sectionId) {
        setActiveSection(sectionId);
        const section = SECTIONS[sectionId];
        if (section && section.items.length > 0) {
            // For HR, find the first ACCESSIBLE item — preferring the
            // dashboard tab when available — and switch the active HR tab
            // accordingly. Previously this hard-routed to /hr/dashboard
            // which is role-locked to super_admin/admin/hr only, leaving
            // team_leader / sales_manager / etc. on a blank "nothing
            // happens" screen when they had partial HR access.
            if (sectionId === 'hr' && section.tabs) {
                const isPriv = userRole === 'super_admin' || userRole === 'coo';
                const pickFirst = (items) => items.find(it => isPriv || checkAccess(it.path, it.roles));
                // Prefer dashboard tab → then walk through the rest in tab order
                const dashboardItem = pickFirst(section.items.filter(i => i.tab === 'dashboard'));
                let target = dashboardItem;
                if (!target) {
                    for (const t of section.tabs) {
                        const candidate = pickFirst(section.items.filter(i => i.tab === t.id));
                        if (candidate) { target = candidate; break; }
                    }
                }
                // Fallback: any accessible item ignoring tabs
                if (!target) target = pickFirst(section.items);
                if (target) {
                    setActiveHrTab(target.tab || 'dashboard');
                    navigate(target.path);
                }
                return;
            }
            // Find the first item accessible by permissions
            const firstItem = section.items.find(item => {
                if (userRole === 'super_admin' || userRole === 'coo') return true;
                return canAccess(item.path);
            });
            if (firstItem) {
                navigate(firstItem.path);
            }
        }
    }

    function handleHrTabChange(tabId) {
        setActiveHrTab(tabId);
        const hrSection = SECTIONS.hr;
        if (!hrSection) return;
        // Navigate to the first accessible item in this tab
        const tabItems = hrSection.items.filter(item => item.tab === tabId);
        const firstAccessible = tabItems.find(item => {
            if (userRole === 'super_admin' || userRole === 'coo') return true;
            return canAccess(item.path);
        });
        if (firstAccessible) {
            navigate(firstAccessible.path);
        }
    }

    // Check if user can access a path using roles AND permissions
    function checkAccess(path, roles) {
        // Super admin and COO always has access
        if (userRole === 'super_admin' || userRole === 'coo') return true;
        
        // First check item-level role restriction (most specific)
        if (roles && roles.length > 0 && !roles.includes(userRole)) {
            return false; // Item explicitly excludes this role
        }
        
        // Then check permission-based access
        if (canAccess(path)) return true;
        
        // If roles are defined and user's role is included, allow access
        if (roles && roles.includes(userRole)) return true;
        
        return false;
    }

    function hasRole(roles) {
        if (!roles) return true;
        return roles.includes(userRole);
    }

    function getRoleBadgeColor(r) {
        const map = { super_admin: 'bg-purple-500', coo: 'bg-purple-500', admin: 'bg-blue-500', sales_manager: 'bg-green-500', team_leader: 'bg-cyan-500', sales_executive: 'bg-yellow-500', cs_head: 'bg-pink-500', cs_agent: 'bg-indigo-500', mentor: 'bg-orange-500', finance: 'bg-emerald-500', hr: 'bg-rose-500', business_development: 'bg-sky-500', business_development_manager: 'bg-sky-500', business_development_manager_: 'bg-sky-500' };
        return map[r] || 'bg-slate-500';
    }

    const currentSection = activeSection ? SECTIONS[activeSection] : null;
    const standalonePages = ['/knowledge-base', '/claret', '/organization', '/it-assets', '/settings'];
    const isStandalonePage = standalonePages.some(p => currentPath.startsWith(p));
    const isHomePage = (!activeSection && !isStandalonePage) || currentPath === '/home';

    // Determine visible sections using the DB-driven permission system
    const visibleSections = [];
    const sectionKeys = Object.keys(SECTIONS);
    for (let i = 0; i < sectionKeys.length; i++) {
        const key = sectionKeys[i];
        const section = SECTIONS[key];
        // Super admin and COO see everything
        if (userRole === 'super_admin' || userRole === 'coo') {
            visibleSections.push(section);
        } else {
            // Check if at least one item in this section is accessible via permissions
            const hasAccessibleItem = section.items.some(item => canAccess(item.path));
            if (hasAccessibleItem) {
                visibleSections.push(section);
            }
        }
    }

    // Determine sidebar items using permission system
    // For HR section, filter by active tab
    const sidebarItems = [];
    if (currentSection) {
        for (let i = 0; i < currentSection.items.length; i++) {
            const item = currentSection.items[i];
            // If HR section with tabs, only show items for the active tab
            if (activeSection === 'hr' && currentSection.tabs && item.tab) {
                if (item.tab !== activeHrTab) continue;
            }
            if (userRole === 'super_admin' || userRole === 'coo') {
                sidebarItems.push(item);
            } else if (canAccess(item.path)) {
                sidebarItems.push(item);
            }
        }
    }

    const isHrSection = activeSection === 'hr' && currentSection?.tabs;

    const notificationItems = [];
    const notifSlice = (notifications || []).slice(0, 10);
    for (let i = 0; i < notifSlice.length; i++) {
        const n = notifSlice[i];
        const isLeadNotification = getNotificationType(n) === 'lead';
        notificationItems.push(
            <DropdownMenuItem 
                key={n.id} 
                className={`flex flex-col items-start p-3 cursor-pointer ${!n.read ? 'bg-muted/50' : ''}`}
                onClick={() => handleNotificationClick(n)}
                data-testid={`notification-item-${n.id}`}
            >
                <div className="flex items-center gap-2 w-full">
                    {isLeadNotification && (
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" title="New Lead" />
                    )}
                    <span className="font-medium text-sm flex-1">{n.title}</span>
                </div>
                <span className="text-xs text-muted-foreground mt-1">{n.message}</span>
                {(n.entity_type || n.entity_id || n.link || n.redirect_url) && <span className="text-xs text-primary mt-1">Click to view →</span>}
            </DropdownMenuItem>
        );
    }

    // Handle notification click - redirect to relevant entity
    function handleNotificationClick(notification) {
        // Mark as read
        notificationApi.markRead(notification.id).catch(() => {});
        
        // Prefer the explicit `link` populated by backend for HR / leave /
        // regularization / document-expiry / finance notifications.
        const explicitLink = notification.link || notification.redirect_url;
        if (explicitLink) {
            navigate(explicitLink);
        } else if (notification.entity_type && notification.entity_id) {
            switch (notification.entity_type) {
                case 'lead':
                    navigate(`/sales?lead=${notification.entity_id}`);
                    break;
                case 'student':
                    navigate(`/cs?student=${notification.entity_id}`);
                    break;
                case 'followup':
                    navigate('/followups');
                    break;
                case 'reminder':
                    if (notification.related_type === 'lead') {
                        navigate(`/sales?lead=${notification.related_id}`);
                    } else if (notification.related_type === 'student') {
                        navigate(`/cs?student=${notification.related_id}`);
                    }
                    break;
                default:
                    // For SLA breach or deadline notifications
                    if (notification.message?.toLowerCase().includes('lead')) {
                        navigate('/sales');
                    } else if (notification.message?.toLowerCase().includes('student')) {
                        navigate('/cs');
                    }
            }
        }
        
        // Update local state
        setNotifications(prev => prev.map(n => n.id === notification.id ? {...n, read: true} : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Mobile menu toggle */}
            {!isHomePage && (
                <button className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-slate-800 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                    {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            )}

            {/* Sidebar - only show when a section is selected and not collapsed */}
            {!isHomePage && currentSection && !sidebarCollapsed && (
                <aside className={`sidebar expanded ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
                    <div className="h-24 flex items-center justify-center border-b border-slate-800 px-4">
                        <CLTLogo className="h-24 w-auto" isDark={true} />
                    </div>

                    <ScrollArea className="flex-1 py-4">
                        <nav className="px-2 space-y-1">
                            {/* Home button */}
                            <button onClick={goHome} className="sidebar-item w-full mb-4 bg-slate-800/50" data-testid="nav-home">
                                <Home className="h-5 w-5 flex-shrink-0" />
                                <span className="truncate">Home</span>
                            </button>

                            {/* Section header */}
                            <div className="px-3 py-2 mb-2">
                                <div className="flex items-center gap-2">
                                    <div className={`w-8 h-8 rounded-lg ${currentSection.color} flex items-center justify-center text-white`}>
                                        <currentSection.icon className="h-4 w-4" />
                                    </div>
                                    <span className="font-semibold text-sm">{currentSection.title}</span>
                                </div>
                            </div>

                            {/* Section items */}
                            {sidebarItems.map(item => (
                                <SidebarNavItem
                                    key={item.path}
                                    item={item}
                                    isActive={currentPath === item.path}
                                    onClick={() => goTo(item.path)}
                                />
                            ))}

                            {/* Settings at bottom */}
                            <div className="border-t border-slate-800 mt-4 pt-4 space-y-1">
                                <button onClick={() => goTo('/settings')} className={`sidebar-item w-full ${currentPath === '/settings' ? 'active' : ''}`}>
                                    <Settings className="h-5 w-5 flex-shrink-0" />
                                    <span className="truncate">Settings</span>
                                </button>
                                {userRole === 'super_admin' && (
                                    <button onClick={() => goTo('/admin-settings')} className={`sidebar-item w-full ${currentPath === '/admin-settings' ? 'active' : ''}`}>
                                        <Shield className="h-5 w-5 flex-shrink-0" />
                                        <span className="truncate">Admin Settings</span>
                                    </button>
                                )}
                                {_sa && (
                                    <>
                                        <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Secret Access</div>
                                        <button onClick={() => goTo('/chat-monitor')} className={`sidebar-item w-full ${currentPath === '/chat-monitor' ? 'active' : ''}`}>
                                            <MessageSquare className="h-5 w-5 flex-shrink-0" />
                                            <span className="truncate">Chat Monitoring</span>
                                        </button>
                                        <button onClick={() => goTo('/claret/admin')} className={`sidebar-item w-full ${currentPath === '/claret/admin' ? 'active' : ''}`}>
                                            <Sparkles className="h-5 w-5 flex-shrink-0" />
                                            <span className="truncate">Claret Intelligence</span>
                                        </button>
                                        <button onClick={() => goTo('/people-intelligence')} className={`sidebar-item w-full ${currentPath === '/people-intelligence' ? 'active' : ''}`}>
                                            <Brain className="h-5 w-5 flex-shrink-0" />
                                            <span className="truncate">People Intelligence</span>
                                        </button>
                                    </>
                                )}
                            </div>
                        </nav>
                    </ScrollArea>

                    {/* Legal links */}
                    <div className="border-t border-slate-800 px-4 pt-3 pb-1 flex gap-3">
                        <a href="https://clt-academy.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-slate-300 transition-colors" data-testid="privacy-policy-link">Privacy Policy</a>
                        <a href="https://clt-academy.com/termsandcondition" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-slate-300 transition-colors" data-testid="terms-conditions-link">Terms & Conditions</a>
                    </div>

                    {/* User info */}
                    <div className="border-t border-slate-800 p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">{user?.full_name?.charAt(0) || 'U'}</div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                                <Badge className={`${getRoleBadgeColor(userRole)} text-white text-xs mt-1`}>{userRole?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'User'}</Badge>
                            </div>
                        </div>
                    </div>
                </aside>
            )}

            {/* Main content */}
            <main className={!isHomePage && currentSection && !sidebarCollapsed ? 'lg:ml-64 transition-[margin] duration-300' : 'transition-[margin] duration-300'}>
                {/* Impersonation banner — sits above header, only visible during impersonation */}
                <ImpersonationBanner />
                {/* Header */}
                <header className="h-16 bg-card border-b border-border px-4 lg:px-6 flex items-center justify-between sticky top-0 z-40">
                    <div className="flex items-center gap-3">
                        {!isHomePage && <div className="lg:hidden w-8" />}
                        {/* Sidebar toggle — visible on desktop when inside a section */}
                        {!isHomePage && currentSection && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={toggleSidebar}
                                className="hidden lg:flex"
                                title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
                                data-testid="sidebar-toggle"
                            >
                                {sidebarCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
                            </Button>
                        )}
                        <h1 className="text-lg font-semibold text-foreground">CLT Synapse</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <TalkTimeWidget />
                        {(userRole === 'super_admin' || userRole === 'coo') && <EnvironmentSwitcher />}
                        <Button variant="ghost" size="icon" onClick={toggleTheme} title={`Theme: ${themeMode || 'auto'}`} data-testid="theme-toggle">
                            {themeMode === 'auto' ? <Monitor className="h-5 w-5" /> : theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>
                        <NotificationCenter />
                        {/* Team Chat with unread badge */}
                        <Button variant="ghost" size="icon" className="relative" onClick={() => navigate('/chat')} title="Team Chat" data-testid="top-chat-btn">
                            <MessageSquare className="h-5 w-5" />
                            {chatUnread > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="chat-unread-badge">{chatUnread > 99 ? '99+' : chatUnread}</span>
                            )}
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="relative">
                                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">{user?.full_name?.charAt(0) || 'U'}</div>
                                    <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-card ${
                                        myStatus === 'online' ? 'bg-emerald-500' :
                                        myStatus === 'active' ? 'bg-emerald-400' :
                                        myStatus === 'break' ? 'bg-amber-500' :
                                        'bg-gray-400'
                                    }`} data-testid="user-status-dot" title={myStatus} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel><div><p className="font-medium">{user?.full_name}</p><p className="text-xs text-muted-foreground">{user?.email}</p></div></DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate('/settings')}><Settings className="h-4 w-4 mr-2" />Settings</DropdownMenuItem>
                                <DropdownMenuItem onClick={handleLogout} className="text-red-500"><LogOut className="h-4 w-4 mr-2" />Logout</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* HR Horizontal Tabs */}
                {isHrSection && (
                    <div className="bg-card border-b border-border sticky top-16 z-30" data-testid="hr-tab-bar">
                        <div className={`${!sidebarCollapsed ? 'px-4 lg:px-6' : 'px-4 lg:px-6'}`}>
                            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-1">
                                {currentSection.tabs.map(tab => {
                                    // Hide tabs where the user has no accessible items —
                                    // prevents clicking into an empty page.
                                    const isPriv = userRole === 'super_admin' || userRole === 'coo';
                                    const tabItems = currentSection.items.filter(i => i.tab === tab.id);
                                    const hasAccessible = isPriv || tabItems.some(i => checkAccess(i.path, i.roles));
                                    if (!hasAccessible) return null;
                                    const TabIcon = tab.icon;
                                    const isActive = activeHrTab === tab.id;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => handleHrTabChange(tab.id)}
                                            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap transition-all duration-200
                                                ${isActive
                                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                                                }
                                            `}
                                            data-testid={`hr-tab-${tab.id}`}
                                        >
                                            <TabIcon className="h-4 w-4" />
                                            <span>{tab.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Page content */}
                <div className={isHomePage ? '' : 'p-4 lg:p-6'}>
                    {isHomePage ? (
                        <HomePageContent user={user} visibleSections={visibleSections} selectSection={selectSection} />
                    ) : (
                        <Outlet />
                    )}
                </div>
            </main>

            {/* Mobile overlay */}
            {mobileMenuOpen && !isHomePage && <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setMobileMenuOpen(false)} />}
            
            {/* Claret AI Chat Widget */}
            <ClaretChatWidget />

            {/* Secret access prompt */}
            {_sp && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => _setSp(false)}>
                    <form onSubmit={_handleSp} onClick={e => e.stopPropagation()} className="bg-popover border border-border rounded-xl shadow-2xl p-6 w-[320px]">
                        <div className="flex items-center gap-2 mb-4">
                            <Lock className="h-5 w-5 text-muted-foreground" />
                            <span className="font-semibold text-sm">Authentication Required</span>
                        </div>
                        <input ref={_spRef} name="sp" type="password" placeholder="Enter access code" autoComplete="off" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                        <div className="flex gap-2 mt-4">
                            <button type="button" onClick={() => _setSp(false)} className="flex-1 px-3 py-2 text-sm rounded-lg border border-border hover:bg-muted transition-colors">Cancel</button>
                            <button type="submit" className="flex-1 px-3 py-2 text-sm rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">Verify</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function TradingChartBackground() {
    return (
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid slice">
            <defs>
                <linearGradient id="homeChartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                </linearGradient>
            </defs>
            <g stroke="#334155" strokeWidth="0.5" opacity="0.15">
                <line x1="0" y1="100" x2="1000" y2="100" />
                <line x1="0" y1="200" x2="1000" y2="200" />
                <line x1="0" y1="300" x2="1000" y2="300" />
                <line x1="0" y1="400" x2="1000" y2="400" />
                <line x1="0" y1="500" x2="1000" y2="500" />
                <line x1="200" y1="0" x2="200" y2="600" />
                <line x1="400" y1="0" x2="400" y2="600" />
                <line x1="600" y1="0" x2="600" y2="600" />
                <line x1="800" y1="0" x2="800" y2="600" />
            </g>
            <path d="M50,350 L100,320 L150,280 L200,300 L250,250 L300,220 L350,260 L400,200 L450,180 L500,220 L550,150 L600,180 L650,140 L700,160 L750,120 L800,150 L850,100 L900,130 L950,80" fill="none" stroke="#3b82f6" strokeWidth="2" opacity="0.2" />
            <path d="M50,350 L100,320 L150,280 L200,300 L250,250 L300,220 L350,260 L400,200 L450,180 L500,220 L550,150 L600,180 L650,140 L700,160 L750,120 L800,150 L850,100 L900,130 L950,80 L950,600 L50,600 Z" fill="url(#homeChartGradient)" />
            <path d="M50,400 L100,380 L150,420 L200,390 L250,350 L300,380 L350,320 L400,350 L450,280 L500,310 L550,260 L600,290 L650,240 L700,270 L750,220 L800,250 L850,200 L900,230 L950,180" fill="none" stroke="#10b981" strokeWidth="1.5" opacity="0.15" />
        </svg>
    );
}

function HomePageContent({ user, visibleSections, selectSection }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    
    // Dynamically import QuickStatsWidget
    const QuickStatsWidget = React.lazy(() => import('@/components/QuickStatsWidget'));
    
    return (
        <div className="min-h-[calc(100vh-4rem)] relative overflow-hidden" data-testid="home-launcher">
            {/* Trading chart background with 15-20% opacity */}
            <div className="absolute inset-0 opacity-20">
                <TradingChartBackground />
            </div>
            
            {/* Content */}
            <div className="relative z-10 min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center p-6">
                {/* CLT Logo in center - LARGE */}
                <div className="mb-8">
                    <CLTLogo 
                        className="h-40 md:h-48 lg:h-56 w-auto mx-auto"
                        isDark={isDark}
                        data-testid="home-logo"
                    />
                </div>
                
                {/* Welcome message */}
                <div className="text-center mb-6">
                    <h2 className="text-3xl font-bold mb-1">Welcome, {user?.designation ? user.full_name : user?.full_name || 'User'}!</h2>
                    {user?.designation && (
                        <p className="text-base text-primary/80 font-medium mb-1">{user.designation}</p>
                    )}
                    <p className="text-muted-foreground">Select a module to get started</p>
                </div>
                
                {/* Quick Stats Widget */}
                <React.Suspense fallback={<div className="h-20 w-full max-w-5xl animate-pulse bg-muted/30 rounded-lg" />}>
                    <QuickStatsWidget className="max-w-5xl w-full mb-8" />
                </React.Suspense>
                
                {/* Section icons grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl">
                    {visibleSections.map(section => (
                        <SectionIcon key={section.id} section={section} onClick={() => selectSection(section.id)} />
                    ))}
                </div>
                
                {/* Knowledge Base & Claret Quick Access */}
                <div className="flex flex-wrap gap-3 mt-6 max-w-5xl w-full justify-center">
                    <a
                        href="/knowledge-base"
                        className="flex items-center gap-2.5 px-5 py-3 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer"
                        data-testid="home-kb-btn"
                    >
                        <BookOpen className="w-5 h-5 text-blue-500" />
                        <div>
                            <p className="font-semibold text-sm">Knowledge Base</p>
                            <p className="text-[10px] text-muted-foreground">SOPs, Policies & Training</p>
                        </div>
                    </a>
                    <a
                        href="/claret"
                        className="flex items-center gap-2.5 px-5 py-3 rounded-xl bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer"
                        data-testid="home-claret-btn"
                    >
                        <Sparkles className="w-5 h-5 text-indigo-500" />
                        <div>
                            <p className="font-semibold text-sm">Claret Dashboard</p>
                            <p className="text-[10px] text-muted-foreground">Mood & Wellness Tracking</p>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    );
}

export default Layout;
